import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from 'Text';
import { SidebarBoxKey } from 'AdmissionsGuidance/StudentDashboard.types';
import { useCurrentUser, useDateHelper } from 'FrontRoyalAngular';
import { getCohort } from 'Users';
import { ProgramTypeConfigs } from 'Program';
import { InlineLink } from 'InlineLink';
import { LogoBox } from '../../LogoBox';

function RegisteredBoxComponent({ mobile }: { mobile?: boolean }) {
    const currentUser = useCurrentUser();
    const dateHelper = useDateHelper();
    const cohort = useMemo(() => currentUser && getCohort(currentUser), [currentUser]);

    const startDate = useMemo(() => {
        if (!cohort) return null;
        return dateHelper.formattedUserFacingMonthDayYearLong(new Date(cohort.startDate * 1000), false);
    }, [cohort, dateHelper]);

    const programTitle = useMemo(() => {
        if (!cohort) return null;
        const config = ProgramTypeConfigs[cohort.programType];
        return config.programBoxTitle?.(cohort) ?? config.fullTitle(cohort);
    }, [cohort]);
    const shortProgramTitle = useMemo(() => programTitle?.replace(/^The\s/, 'the '), [programTitle]);

    const programGuideUrl = useMemo(() => {
        if (!cohort) return null;
        return cohort.programGuideUrl;
    }, [cohort]);

    if (!programTitle || !shortProgramTitle || !startDate) return null;

    return mobile ? (
        <RegisteredBoxMobilePresentation />
    ) : (
        <RegisteredBoxPresentation
            programTitle={programTitle}
            shortProgramTitle={shortProgramTitle}
            startDate={startDate}
            programGuideUrl={programGuideUrl}
        />
    );
}

function RegisteredBoxPresentation({
    programTitle,
    shortProgramTitle,
    startDate,
    programGuideUrl,
}: {
    programTitle: string;
    shortProgramTitle: string;
    startDate: string;
    programGuideUrl: string | null;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'registeredBox' });
    return (
        <LogoBox title={programTitle} boxKey={SidebarBoxKey.registered} color="primary">
            <Text as="p">{t('content', { programTitle: shortProgramTitle })}</Text>
            <Text as="p">{t('content2', { startDate })}</Text>
            {programGuideUrl && (
                <Text as="p">
                    {t('contentProgramGuide', {
                        programGuideLink: <InlineLink href={programGuideUrl}>{t('programGuide')}</InlineLink>,
                    })}
                </Text>
            )}
        </LogoBox>
    );
}

function RegisteredBoxMobilePresentation() {
    // Not shown in mobile
    return null;
}

export const RegisteredBox = memo(RegisteredBoxComponent);
